import { deelVan, duurVan, maakProfiel, volg } from './beweging'
import type { Stap } from './beweging'
import { wachtenOpOmloop } from '../omloop'

/**
 * De T- en L-MATIC: één pallet heen en weer tussen twee stations (feedback 21
 * oktober, "realistischer").
 *
 * Eerst reden ze een werkgang van 10 m, draaiden ze aan het eind in één beeld
 * 180° om en verscheen hun lading uit het niets (de L-MATIC hefde zelfs
 * nooit iets). Nu draait de truck nooit: de ene rit vorken voorop, de
 * terugrit met de aandrijving voorop, zoals de K- en R-MATIC al deden. En er
 * is één pallet, die hij werkelijk neerzet en weer oppakt:
 *
 *   kopstation (vorkkant): vorken voorop erheen, pallet neerzetten, achteruit
 *   eruit, wachten, er weer in, pallet oppakken;
 *   staartstation (aandrijfkant): achteruit erheen, pallet neerzetten, wachten
 *   (L: eerst achteruit eruit), pallet oppakken.
 *
 * De T-MATIC zet hem aan beide kanten op de vloer, de L-MATIC aan de verre
 * kant op een overslagstation van 1 m (productiebevoorrading) en aan de
 * camerakant op de vloer.
 *
 * Alles is een functie van de tijd, zonder toestand, net als `werkcyclus.ts`.
 * De pallet gaat over op het einde van een stap, waar truck en vork
 * stilstaan, en precies op de plek waar het station hem verwacht. Puur, zonder
 * three.js: `scripts/controleer-bewegingen.mjs` toetst dit.
 */

export interface HeenEnWeerTempo {
  /** Rijsnelheid met en zonder pallet, m/s. */
  rijden: { last: number; leeg: number }
  /** Het stukje de pallet in of uit. */
  kruip: number
  a: number
  heffen: { last: number; leeg: number }
  dalen: { last: number; leeg: number }
  hefA: number
}

export interface HeenEnWeerTruck {
  /** Richting waarin de vorken wijzen, langs x. */
  zin: 1 | -1
  /** Midden van de pallet op de vorken, vanaf de oorsprong van de truck. */
  p: number
  /** Hoe ver hij achteruit rijdt om de vorken uit de pallet te halen. */
  uitrij: number
  /** Van de oorsprong tot de achterkant van de aandrijving. */
  achter: number
  /** Bovenkant van de vorken in de laagste stand, boven de vloer. */
  vorkTop: number
  /** Vorkhoogte (boven de laagste stand) bij het rijden. */
  rij: number
  /** Vorkhoogte waarop het blad onder de dwarsplanken komt: de pallet raakt. */
  raak: number
  /** Hoogte van de pallet op het kopstation: 0 is de vloer, anders een overslagstation. */
  kopHoogte: number
  /** Bij het staartstation achteruit uitrijden en wachten, of met de vorken erin wachten. */
  staartUitrijden: boolean
  tempo: HeenEnWeerTempo
}

/** Onderkant van de dwarsplanken van een europallet, vanaf de 800-kant: daar rust hij op de vorken. */
export const DWARSPLANK = 0.1

type Kanaal = 'x' | 'hoogte'
type Gebeurtenis = 'gezet:kop' | 'gezet:staart' | 'gepakt'

export interface HeenEnWeer {
  stappen: Stap<Kanaal>[]
  duur: number
  begin: Record<Kanaal, number>
  /** Waar de pallet staat op het kop- en het staartstation (midden, en onderkant). */
  kopX: number
  kopHoogte: number
  staartX: number
}

/**
 * `opOmloop`: de wachttijden zo dat de rondgang een geheel aantal keer in de
 * omloop van de hal past (`omloop.ts`). Aan in de hal van de film, met de
 * Matics als Linde-beeld.
 */
export function maakHeenEnWeer(t: HeenEnWeerTruck, anker: number, bereik: number, opOmloop = false): HeenEnWeer {
  const { zin, p } = t
  const eindVork = anker + zin * bereik
  const eindAandrijf = anker - zin * bereik
  // De pallet staat 0,6 m binnen het eind van de werkgang: daar ligt zijn
  // vloerkader of de stellage.
  const kopX = eindVork - zin * 0.6
  const Xk = kopX - zin * p
  const Xk2 = Xk - zin * t.uitrij
  // Aan de aandrijfkant zet hij hem neer waar hij stilstaat; met uitrijden
  // erbij blijft ook zijn achterkant binnen de werkgang.
  const Xs = eindAandrijf + zin * (t.achter + (t.staartUitrijden ? t.uitrij : 0))
  const staartX = Xs + zin * p
  const hoog = t.kopHoogte > 0
  // Vorkhoogtes bij het kopstation: op een stellage eerst boven de liggers
  // erin, de pallet 6 cm los erboven, en eruit met de bovenkant van het blad
  // 3 cm onder de dwarsplanken en de onderkant 15 mm boven de ligger.
  const kop = hoog
    ? { raak: t.kopHoogte + t.raak, boven: t.kopHoogte + t.raak + 0.06, vrij: t.kopHoogte - 0.016 }
    : { raak: t.raak, boven: t.rij, vrij: 0 }

  const stappen: Stap<Kanaal>[] = []
  const begin = { x: Xs, hoogte: t.rij }
  const nu = { ...begin }
  // De rondgang begint aan de staartkant, met de pallet op de vorken.
  let geladen = true
  const stap = (doel: Partial<Record<Kanaal, number>>, weg: number, v: number, a: number, minDuur: number, gebeurt?: Gebeurtenis) => {
    stappen.push({ doel, profiel: maakProfiel(weg, v, a, minDuur), gebeurt })
    Object.assign(nu, doel)
    if (gebeurt) geladen = gebeurt === 'gepakt'
  }
  const rij = (x: number) => stap({ x }, Math.abs(x - nu.x), geladen ? t.tempo.rijden.last : t.tempo.rijden.leeg, t.tempo.a, 0.4)
  const kruip = (x: number) => stap({ x }, Math.abs(x - nu.x), t.tempo.kruip, t.tempo.a, 0.4)
  const hef = (h: number, gebeurt?: Gebeurtenis) => {
    const tempo = h > nu.hoogte ? t.tempo.heffen : t.tempo.dalen
    stap({ hoogte: h }, Math.abs(h - nu.hoogte), geladen ? tempo.last : tempo.leeg, t.tempo.hefA, 0.4, gebeurt)
  }
  const wachten: Stap<Kanaal>[] = []
  const wacht = (s: number) => {
    stap({}, 0, 1, 1, s)
    wachten.push(stappen[stappen.length - 1])
  }

  // Naar het kopstation, vorken voorop.
  if (hoog) {
    rij(Xk2)
    hef(kop.boven)
    kruip(Xk)
  } else {
    rij(Xk)
  }
  hef(kop.raak, 'gezet:kop')
  hef(kop.vrij)
  kruip(Xk2)
  wacht(2)
  kruip(Xk)
  hef(kop.raak, 'gepakt')
  hef(kop.boven)
  if (hoog) {
    kruip(Xk2)
    hef(t.rij)
  }

  // Terug naar het staartstation, aandrijving voorop.
  rij(Xs)
  hef(t.raak, 'gezet:staart')
  hef(0)
  if (t.staartUitrijden) {
    kruip(Xs - zin * t.uitrij)
    wacht(2)
    kruip(Xs)
  } else {
    wacht(2)
  }
  hef(t.raak, 'gepakt')
  hef(t.rij)

  if (opOmloop) zetWachten(stappen, wachten)
  return { stappen, duur: duurVan(stappen), begin, kopX, kopHoogte: t.kopHoogte, staartX }
}

/** De wachtstappen op hun nieuwe duur, zodat de rondgang op de omloop past. */
function zetWachten<K extends string>(stappen: Stap<K>[], wachten: Stap<K>[]) {
  const nieuw = wachtenOpOmloop(duurVan(stappen), wachten.map((w) => w.profiel.duur))
  wachten.forEach((w, i) => { w.profiel = maakProfiel(0, 1, 1, nieuw[i]) })
}

/**
 * De K- en R-MATIC als Linde-beeld (21 oktober): een render heeft één vaste
 * mast- en vorkstand, dus geen heffen in de stelling. Ze rijden tussen hun
 * twee werkpunten heen en weer en wachten aan elk eind, zonder te draaien:
 * naar B met de aandrijving voorop, naar A met de vorken voorop, op de
 * snelheden van het typeblad. Zelfde vorm als `maakHeenEnWeer`, zonder
 * gebeurtenissen: wat hij draagt, blijft op de vorken.
 *
 * Altijd op de omloop (`omloop.ts`): deze rit bestaat alleen in de hal van
 * de film. De vijf seconden wachten aan elk eind worden daardoor iets meer
 * of minder.
 */
export function maakVasteRit(
  rijden: { naarB: number; naarA: number; a: number }, xa: number, xb: number, wachten = 5,
): HeenEnWeer {
  const begin = { x: xa, hoogte: 0 }
  const weg = Math.abs(xb - xa)
  const opB: Stap<Kanaal> = { doel: {}, profiel: maakProfiel(0, 1, 1, wachten) }
  const opA: Stap<Kanaal> = { doel: {}, profiel: maakProfiel(0, 1, 1, wachten) }
  const stappen: Stap<Kanaal>[] = [
    { doel: { x: xb }, profiel: maakProfiel(weg, rijden.naarB, rijden.a, 0.4) },
    opB,
    { doel: { x: xa }, profiel: maakProfiel(weg, rijden.naarA, rijden.a, 0.4) },
    opA,
  ]
  zetWachten(stappen, [opB, opA])
  return { stappen, duur: duurVan(stappen), begin, kopX: xb, kopHoogte: 0, staartX: xa }
}

export interface HeenEnWeerMoment {
  /** Oorsprong van de truck langs x, en de vorkhoogte boven de laagste stand. */
  x: number
  hoogte: number
  /** Rijsnelheid langs x, m/s. */
  vx: number
  /** Waar de pallet is. */
  pallet: 'vork' | 'kop' | 'staart'
}

/** Waar de truck is en wat hij doet op tijd τ (seconden). */
export function momentHeenEnWeer(h: HeenEnWeer, tau: number): HeenEnWeerMoment {
  const t = ((tau % h.duur) + h.duur) % h.duur
  const { kanalen, laatste } = volg(h.stappen, h.begin, t)
  return {
    x: kanalen.x,
    hoogte: kanalen.hoogte,
    vx: snelheidX(h, t),
    // Nog niets af in deze rondgang: dan staat hij er zoals de vorige eindigde,
    // met de pallet op de vorken.
    pallet: laatste === 'gezet:kop' ? 'kop' : laatste === 'gezet:staart' ? 'staart' : 'vork',
  }
}

/** Rijsnelheid langs x, uit de stap die op tijd t loopt. */
function snelheidX(h: HeenEnWeer, t: number): number {
  let tijd = 0
  let x = h.begin.x
  for (const s of h.stappen) {
    if (t < tijd + s.profiel.duur) {
      if (s.doel.x === undefined || s.profiel.weg === 0) return 0
      const dt = 1e-3
      const lokaal = t - tijd
      const d = (deelVan(s.profiel, Math.min(lokaal + dt, s.profiel.duur)) - deelVan(s.profiel, Math.max(lokaal - dt, 0))) / (2 * dt)
      return (s.doel.x - x) * d
    }
    if (s.doel.x !== undefined) x = s.doel.x
    tijd += s.profiel.duur
  }
  return 0
}
